import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { CategoryChips } from "@/components/PageBlocks";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Search, ArrowRight, Calendar, Clock, ArrowLeft,
    LayoutGrid, BarChart3, Wallet, MapPinned, Building2, Scale, Plane, Newspaper, Mail,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=2400&q=80";

const HERO_STATS = [
    { icon: BarChart3,   v: "250+", l: "Market Reports" },
    { icon: MapPinned,   v: "50+",  l: "Expert Authors" },
    { icon: Building2,   v: "15+",  l: "Cities Covered" },
    { icon: Newspaper,   v: "10K+", l: "Readers Monthly" },
];

const CATEGORIES = [
    { key: "all",             label: "All Insights",       sub: "View All",             icon: LayoutGrid },
    { key: "market_report",   label: "Market Reports",     sub: "Trends & Analysis",    icon: BarChart3  },
    { key: "investment",      label: "Investment Guides",  sub: "ROI & Strategy",       icon: Wallet     },
    { key: "area",            label: "Area Insights",      sub: "Locality Deep Dives",  icon: MapPinned  },
    { key: "builder_review",  label: "Builder Reviews",    sub: "Ratings & Analysis",   icon: Building2  },
    { key: "legal_update",    label: "Legal Updates",      sub: "Laws & Policies",      icon: Scale      },
    { key: "nri",             label: "NRI Corner",         sub: "Guides for NRIs",      icon: Plane      },
    { key: "news",            label: "News & Updates",     sub: "Latest News",          icon: Newspaper  },
];

const CITY_CARDS = [
    { name: "Chandigarh",  count: "120+",  img: "https://images.pexels.com/photos/1029611/pexels-photo-1029611.jpeg?w=1200" },
    { name: "Mohali",      count: "85+",   img: "https://images.pexels.com/photos/2437299/pexels-photo-2437299.jpeg?w=1200" },
    { name: "Delhi NCR",   count: "200+",  img: "https://images.pexels.com/photos/2166711/pexels-photo-2166711.jpeg?w=1200" },
    { name: "Bangalore",   count: "150+",  img: "https://images.pexels.com/photos/2287310/pexels-photo-2287310.jpeg?w=1200" },
    { name: "Pune",        count: "110+",  img: "https://images.pexels.com/photos/2404046/pexels-photo-2404046.jpeg?w=1200" },
    { name: "Mumbai",      count: "130+",  img: "https://images.pexels.com/photos/2846217/pexels-photo-2846217.jpeg?w=1200" },
];

function fmtDate(d) {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
// Accepts our multiple date field names from the API and older mock rows.
const dateOf = (it) => it?.published_at || it?.date || it?.created_at || null;

function categoryMatch(item, key) {
    if (key === "all") return true;
    const c = (item.category || "").toLowerCase().replace(/\s|&/g, "");
    return c.includes(key.replace(/_/g, ""));
}

export default function Insights() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cat, setCat] = useState("all");
    const [q, setQ] = useState("");
    const [subEmail, setSubEmail] = useState("");
    const [subBusy, setSubBusy] = useState(false);

    useEffect(() => {
        api.get("/insights").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        return items.filter((it) => {
            if (!categoryMatch(it, cat)) return false;
            if (q && !`${it.title} ${it.excerpt}`.toLowerCase().includes(q.toLowerCase())) return false;
            return true;
        });
    }, [items, cat, q]);

    const featured   = filtered[0];
    const relatedTop = filtered.slice(1, 4);
    const marketReports    = filtered.filter((it) => (it.category || "").toLowerCase().includes("market") || (it.category || "").toLowerCase().includes("report")).slice(0, 4);
    const investmentGuides = filtered.filter((it) => (it.category || "").toLowerCase().includes("invest") || (it.category || "").toLowerCase().includes("guide")).slice(0, 4);
    const news             = filtered.filter((it) => (it.category || "").toLowerCase().includes("news") || (it.category || "").toLowerCase().includes("update")).slice(0, 4);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!subEmail) return;
        setSubBusy(true);
        try {
            await api.post("/newsletter", { email: subEmail });
            toast.success("Subscribed. Fresh reports in your inbox.");
            setSubEmail("");
        } catch (err) { toast.error(formatApiError(err)); } finally { setSubBusy(false); }
    };

    return (
        <PageShell navVariant="dark">
            {/* ============ HERO ============ */}
            <section data-testid="insights-hero" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45"/>
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0d18]/70 via-[#0a0d18]/60 to-[#0a0d18]"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-16 md:pt-44 md:pb-20">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase mb-5">EBG Realty Insights</div>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-[76px] leading-[1.02] max-w-4xl">
                        Real Estate <em className="not-italic text-[var(--ebg-gold)]">Insights.</em><br/>
                        Real <em className="not-italic text-[var(--ebg-gold)]">Decisions.</em>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 leading-relaxed text-base md:text-lg">
                        Market trends, expert analysis, investment guides and legal updates to help you stay
                        ahead in real estate.
                    </p>
                    {/* Search bar */}
                    <form
                        onSubmit={(e) => e.preventDefault()}
                        data-testid="insights-search-form"
                        className="mt-10 flex gap-0 max-w-2xl bg-white/95 backdrop-blur-md p-1.5"
                    >
                        <input
                            data-testid="insights-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search Insights, reports, news & more…"
                            className="flex-1 px-5 py-4 text-[14px] text-black outline-none bg-transparent"
                        />
                        <button
                            type="submit"
                            data-testid="insights-search-btn"
                            className="bg-[var(--ebg-gold)] text-black px-6 py-4 text-[11px] uppercase tracking-[0.28em] font-bold inline-flex items-center gap-2 hover:bg-black hover:text-[var(--ebg-gold)] transition-colors"
                        >
                            <Search size={14}/> Search
                        </button>
                    </form>
                    {/* Stats */}
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {HERO_STATS.map((s, i) => (
                            <div key={i} data-testid={`insights-hero-stat-${i}`} className="flex items-center gap-4">
                                <span className="w-12 h-12 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center shrink-0">
                                    <s.icon size={18}/>
                                </span>
                                <div>
                                    <div className="display-num text-2xl md:text-3xl text-white tabular-nums leading-none">{s.v}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/60 mt-1.5">{s.l}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ CATEGORY CHIPS ============ */}
            <section data-testid="insights-categories" className="bg-white border-b border-black/8 sticky top-20 z-30">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5">
                    <CategoryChips items={CATEGORIES} activeKey={cat} onSelect={setCat} testidPrefix="ins-cat"/>
                </div>
            </section>

            {/* ============ FEATURED ============ */}
            {loading ? (
                <div className="py-40 text-center text-black/40">Loading insights…</div>
            ) : featured ? (
                <section data-testid="featured-insights" className="bg-white">
                    <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black mb-6">FEATURED INSIGHTS</div>
                        <div className="grid lg:grid-cols-12 gap-6">
                            {/* Big featured */}
                            <Link
                                to={`/insights/${featured.slug}`}
                                data-testid="featured-primary"
                                className="lg:col-span-7 relative group aspect-[4/3] lg:aspect-auto lg:min-h-[560px] overflow-hidden"
                            >
                                <img src={featured.cover_image || featured.image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"/>
                                <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                                    <span className="inline-flex items-center bg-black/70 backdrop-blur-md text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-bold w-fit">{featured.category}</span>
                                    <div className="flex items-center gap-3 text-white/70 text-[12px] mt-4">
                                        <Calendar size={12}/> {fmtDate(dateOf(featured))}
                                    </div>
                                    <h3 className="font-serif text-white text-3xl md:text-4xl lg:text-5xl leading-[1.05] mt-3 max-w-2xl">{featured.title}</h3>
                                    <p className="text-white/75 mt-4 max-w-xl text-[14px] md:text-[15px] leading-relaxed">{featured.excerpt}</p>
                                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">
                                        Read More <ArrowRight size={12}/>
                                    </span>
                                </div>
                            </Link>
                            {/* 3 side cards */}
                            <div className="lg:col-span-5 flex flex-col gap-5">
                                {relatedTop.map((it, i) => (
                                    <Link
                                        key={it.id}
                                        to={`/insights/${it.slug}`}
                                        data-testid={`featured-side-${i}`}
                                        className="group flex gap-4 items-start bg-white border border-black/8 hover:border-black transition-all"
                                    >
                                        <div className="w-40 md:w-48 aspect-square overflow-hidden shrink-0 bg-black/5">
                                            <img src={it.cover_image || it.image} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                                        </div>
                                        <div className="flex-1 p-4">
                                            <span className="inline-flex bg-black text-white px-2 py-1 text-[9px] uppercase tracking-[0.2em] font-bold">{it.category}</span>
                                            <h4 className="font-serif text-base md:text-lg leading-tight mt-3 group-hover:text-[var(--ebg-gold)] transition-colors line-clamp-2">{it.title}</h4>
                                            <p className="text-black/55 text-[12px] mt-2 line-clamp-2">{it.excerpt}</p>
                                            <div className="flex items-center gap-3 text-[11px] text-black/45 mt-3">
                                                <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(dateOf(it))}</span>
                                                <span className="flex items-center gap-1"><Clock size={11}/> {it.read_minutes || 6} min</span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}

            {/* ============ MARKET REPORTS ============ */}
            {marketReports.length > 0 && (
                <SectionGrid title="MARKET REPORTS" items={marketReports} ctaLabel="View All Reports" cat="market_report" onSetCat={setCat}/>
            )}

            {/* ============ INVESTMENT GUIDES ============ */}
            {investmentGuides.length > 0 && (
                <SectionGrid title="INVESTMENT GUIDES" items={investmentGuides} ctaLabel="View All Guides" cat="investment" onSetCat={setCat} bg="ivory"/>
            )}

            {/* ============ LATEST NEWS ============ */}
            {news.length > 0 && (
                <NewsCarousel items={news} onSetCat={setCat}/>
            )}

            {/* ============ EXPLORE BY CITY ============ */}
            <section data-testid="explore-cities" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Explore Insights by City</div>
                            <h2 className="font-serif text-3xl md:text-4xl mt-3">Local intelligence, delivered.</h2>
                        </div>
                        <Link to="/properties" className="text-[11px] uppercase tracking-[0.28em] font-bold text-black link-gold inline-flex items-center gap-2">
                            View All Cities <ArrowRight size={12}/>
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {CITY_CARDS.map((c, i) => (
                            <div key={c.name} data-testid={`city-card-${i}`} className="group relative aspect-[3/4] overflow-hidden bg-black/5">
                                <img src={c.img} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"/>
                                <div className="absolute inset-0 flex flex-col justify-end p-4">
                                    <div className="font-serif text-white text-lg md:text-xl">{c.name}</div>
                                    <div className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold mt-0.5">{c.count} Articles</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ NEWSLETTER STRIP ============ */}
            <section data-testid="insights-newsletter" className="bg-[#0a0d18] text-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-6 flex items-start gap-5">
                        <span className="w-14 h-14 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center shrink-0">
                            <Mail size={20}/>
                        </span>
                        <div>
                            <h3 className="font-serif text-2xl md:text-3xl leading-tight">Stay Updated with Real Estate Insights</h3>
                            <p className="text-white/60 mt-2 text-[14px]">Subscribe to get the latest market reports, investment tips and real estate news.</p>
                        </div>
                    </div>
                    <form onSubmit={subscribe} className="lg:col-span-6 flex gap-2 items-center bg-white/5 border border-white/15 p-2">
                        <input
                            required
                            type="email"
                            data-testid="insights-newsletter-email"
                            value={subEmail}
                            onChange={(e) => setSubEmail(e.target.value)}
                            placeholder="Enter your email address"
                            className="flex-1 bg-transparent px-4 py-3 text-white placeholder:text-white/40 outline-none text-[14px]"
                        />
                        <button
                            type="submit"
                            disabled={subBusy}
                            data-testid="insights-newsletter-btn"
                            className="bg-[var(--ebg-gold)] text-black px-6 py-3 text-[11px] uppercase tracking-[0.28em] font-bold disabled:opacity-60 hover:bg-white transition-colors"
                        >
                            {subBusy ? "…" : "Subscribe"}
                        </button>
                    </form>
                    <div className="lg:col-span-12 text-[11px] text-white/40">No spam. Unsubscribe anytime.</div>
                </div>
            </section>
        </PageShell>
    );
}

function SectionGrid({ title, items, ctaLabel, cat, onSetCat, bg = "white" }) {
    return (
        <section data-testid={`grid-${cat}`} className={bg === "ivory" ? "bg-[var(--ebg-ivory)]" : "bg-white"}>
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black">{title}</div>
                    <button onClick={() => onSetCat(cat)} data-testid={`grid-${cat}-cta`} className="text-[11px] uppercase tracking-[0.28em] font-bold text-black link-gold inline-flex items-center gap-2">
                        {ctaLabel} <ArrowRight size={12}/>
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {items.map((it, i) => (
                        <Link key={it.id} to={`/insights/${it.slug}`} data-testid={`${cat}-card-${i}`} className="group">
                            <div className="relative aspect-square overflow-hidden bg-black/5">
                                <img src={it.cover_image || it.image} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                                <span className="absolute top-3 left-3 bg-black/80 text-white px-2.5 py-1 text-[9px] uppercase tracking-[0.22em] font-bold">{title.includes("REPORT") ? "REPORT" : "GUIDE"}</span>
                            </div>
                            <h3 className="font-serif text-lg md:text-xl leading-tight mt-4 group-hover:text-[var(--ebg-gold)] transition-colors line-clamp-2">{it.title}</h3>
                            <p className="text-black/55 text-[13px] mt-2 line-clamp-2">{it.excerpt}</p>
                            <div className="flex items-center gap-3 text-[11px] text-black/45 mt-3">
                                <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(dateOf(it))}</span>
                                <span className="flex items-center gap-1"><Clock size={11}/> {it.read_minutes || 6} min read</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

function NewsCarousel({ items, onSetCat }) {
    return (
        <section data-testid="news-carousel" className="bg-white border-t border-black/8">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20">
                <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black">LATEST REAL ESTATE NEWS</div>
                    <div className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center text-black/60 hover:border-black hover:text-black cursor-pointer"><ArrowLeft size={14}/></span>
                        <span className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center text-black/60 hover:border-black hover:text-black cursor-pointer"><ArrowRight size={14}/></span>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {items.map((it, i) => (
                        <Link key={it.id} to={`/insights/${it.slug}`} data-testid={`news-card-${i}`} className="group">
                            <div className="aspect-[4/3] overflow-hidden bg-black/5">
                                <img src={it.cover_image || it.image} alt={it.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                            </div>
                            <h3 className="font-serif text-base md:text-lg leading-tight mt-4 group-hover:text-[var(--ebg-gold)] transition-colors line-clamp-2">{it.title}</h3>
                            <div className="text-[11px] text-black/45 mt-2 flex items-center gap-1">
                                <Calendar size={11}/> {fmtDate(dateOf(it))}
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="mt-10 text-center md:text-left">
                    <button onClick={() => onSetCat("news")} data-testid="news-cta" className="text-[11px] uppercase tracking-[0.28em] font-bold text-black link-gold inline-flex items-center gap-2">
                        View All News <ArrowRight size={12}/>
                    </button>
                </div>
            </div>
        </section>
    );
}
